import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ArchivosService } from 'app/services/archivos.service';
import Swal from 'sweetalert2';

interface ProductoPreview {
  codigo: string;
  codigoBarras: string;
  nombre: string;
  tipo: string;
  stock: number;
  stockMin: number;
  stockMax: number;
  moneda: string;
  proveedor: string;
  impuestos: number;
  iva: number;
  precioCompraBase: number;
  precioCompraFinal: number;
  margen: number;
  precioVentaBase: number;
  precioVentaFinal: number;
  estado: 'pendiente' | 'valido' | 'error';
  mensaje?: string;
  esFormulaPrecioCompraFinal?: boolean;
  esFormulaPrecioVentaBase?: boolean;
  esFormulaPrecioVentaFinal?: boolean;
}

@Component({
  selector: 'app-carga-excel-productos',
  templateUrl: './carga-excel-productos.component.html',
  styleUrls: ['./carga-excel-productos.component.css']
})
export class CargaExcelProductosComponent implements OnInit {
  
  archivoSeleccionado: File | null = null;
  nombreArchivo: string = '';
  productosPreview: ProductoPreview[] = [];
  mostrarPreview: boolean = false;
  cargando: boolean = false;
  procesando: boolean = false;
  hasMoreData: boolean = false;
  
  // Estadísticas
  totalRegistros: number = 0;
  registrosValidos: number = 0;
  registrosConError: number = 0;

  constructor(
    private router: Router,
    private archivoServicio: ArchivosService
  ) { }

  ngOnInit(): void {
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    
    if (file) {
      // Validar que sea un archivo Excel
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (extension !== 'xlsx' && extension !== 'xls') {
        Swal.fire({
          icon: 'error',
          title: 'Archivo no válido',
          text: 'Por favor seleccione un archivo Excel (.xlsx o .xls)',
          confirmButtonColor: '#102dd3'
        });
        return;
      }

      this.archivoSeleccionado = file;
      this.nombreArchivo = file.name;
      this.leerArchivoExcel(file);
    }
  }

  leerArchivoExcel(file: File): void {
    this.cargando = true;

    // Crear FormData para enviar al backend
    const formData = new FormData();
    formData.append('file', file);

    // Llamar al endpoint de preview
    this.archivoServicio.getArticulosExcelPreview(formData).subscribe(
      (response) => {
        this.cargando = false;
        
        // Procesar la respuesta del backend
        if (response && response.success) {
          this.procesarDatosDesdeBackend(response);
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error al procesar archivo',
            text: response?.message || 'La respuesta del servidor no contiene datos válidos',
            confirmButtonColor: '#102dd3'
          });
        }
      },
      (error) => {
        this.cargando = false;
        console.error('Error al obtener preview:', error);
        
        Swal.fire({
          icon: 'error',
          title: 'Error al procesar archivo',
          text: error?.error?.message || 'No se pudo procesar el archivo Excel. Verifique que el formato sea correcto.',
          confirmButtonColor: '#102dd3'
        });
      }
    );
  }

  procesarDatosDesdeBackend(response: any): void {
    console.log('Respuesta del backend:', response);
    
    this.productosPreview = [];
    this.totalRegistros = 0;
    this.registrosValidos = 0;
    this.registrosConError = 0;

    // Extraer estadísticas de la respuesta
    this.totalRegistros = response.totalRows || 0;
    this.registrosValidos = response.validRows || 0;
    this.registrosConError = response.errorRows || 0;

    // Crear un mapa de errores por fila para acceso rápido
    const erroresPorFila = new Map<number, string[]>();
    if (response.errors && Array.isArray(response.errors)) {
      response.errors.forEach((errorItem: any) => {
        if (errorItem.row && errorItem.errors) {
          erroresPorFila.set(errorItem.row, errorItem.errors);
        }
      });
    }

    // Procesar los datos
    const data = response.data || [];
    
    data.forEach((item: any, index: number) => {
      // Limitar a 100 registros para el preview
      if (index >= 100) return;

      console.log(`Procesando item ${index}:`, item);

      // Función auxiliar para extraer valores de fórmulas o valores directos
      const extraerValor = (campo: any, tipoFormula: string): { valor: number, esFormula: boolean } => {
        console.log(`Procesando campo ${tipoFormula}:`, campo, 'tipo:', typeof campo);
        
        // Si es null o undefined
        if (campo === null || campo === undefined) {
          return { valor: 0, esFormula: false };
        }
        
        // Si es un número directo
        if (typeof campo === 'number') {
          return { valor: campo, esFormula: false };
        }
        
        // Si es un objeto
        if (typeof campo === 'object' && campo !== null) {
          // Formato: { formula: "=A1*B1", calculatedValue: 123.45 }
          if (campo.formula && campo.calculatedValue !== undefined) {
            return { valor: parseFloat(campo.calculatedValue) || 0, esFormula: true };
          }
          // Formato: { formula: "=A1*B1", value: 123.45 }
          else if (campo.formula && campo.value !== undefined) {
            return { valor: parseFloat(campo.value) || 0, esFormula: true };
          }
          // Formato: { formula: "=A1*B1" } sin valor calculado
          else if (campo.formula) {
            return { valor: 0, esFormula: true };
          }
          // Formato: { value: 123.45 }
          else if (campo.value !== undefined) {
            return { valor: parseFloat(campo.value) || 0, esFormula: false };
          }
          // Formato: { result: 123.45, isFormula: true }
          else if (campo.result !== undefined) {
            return { valor: parseFloat(campo.result) || 0, esFormula: campo.isFormula || false };
          }
        }
        
        // Si es una cadena
        if (typeof campo === 'string') {
          // Si es una fórmula de Excel
          if (campo.startsWith('=')) {
            return { valor: 0, esFormula: true };
          }
          // Si es una cadena numérica
          const numericValue = parseFloat(campo);
          if (!isNaN(numericValue)) {
            return { valor: numericValue, esFormula: false };
          }
          // Si es una cadena vacía
          if (campo.trim() === '') {
            return { valor: 0, esFormula: false };
          }
        }
        
        // Fallback: tratar de convertir a número
        const fallbackValue = parseFloat(campo);
        return { valor: isNaN(fallbackValue) ? 0 : fallbackValue, esFormula: false };
      };

      const precioCompraFinal = extraerValor(item.precioCompraFinal, 'precioCompraFinal');
      const precioVentaBase = extraerValor(item.precioVentaBase, 'precioVentaBase');
      const precioVentaFinal = extraerValor(item.precioVentaFinal, 'precioVentaFinal');

      console.log(`Precios procesados para item ${index}:`, {
        precioCompraFinal,
        precioVentaBase,
        precioVentaFinal,
        original: {
          precioCompraFinal: item.precioCompraFinal,
          precioVentaBase: item.precioVentaBase,
          precioVentaFinal: item.precioVentaFinal
        }
      });

      const producto: ProductoPreview = {
        codigo: item.codigo || '',
        codigoBarras: item.codigoBarras || '',
        nombre: item.nombre || '',
        tipo: item.tipo || '',
        stock: parseFloat(item.stock) || 0,
        stockMin: parseFloat(item.stockMin) || 0,
        stockMax: parseFloat(item.stockMax) || 0,
        moneda: item.moneda || '',
        proveedor: item.proveedor || '',
        impuestos: parseFloat(item.impuestos) || 0,
        iva: parseFloat(item.iva) || 0,
        precioCompraBase: parseFloat(item.precioCompraBase) || 0,
        precioCompraFinal: precioCompraFinal.valor,
        margen: parseFloat(item.margen) || 0,
        precioVentaBase: precioVentaBase.valor,
        precioVentaFinal: precioVentaFinal.valor,
        estado: 'pendiente',
        mensaje: '',
        esFormulaPrecioCompraFinal: precioCompraFinal.esFormula,
        esFormulaPrecioVentaBase: precioVentaBase.esFormula,
        esFormulaPrecioVentaFinal: precioVentaFinal.esFormula
      };

      // Verificar si esta fila tiene errores reportados por el backend
      const rowNumber = item.rowNumber;
      const erroresDeBackend = erroresPorFila.get(rowNumber);

      if (erroresDeBackend && erroresDeBackend.length > 0) {
        producto.estado = 'error';
        producto.mensaje = erroresDeBackend.join(', ');
      } else {
        // Validaciones locales adicionales (opcional, el backend ya validó)
        const errores: string[] = [];
        
        if (!producto.codigo) errores.push('código requerido');
        if (!producto.nombre) errores.push('nombre requerido');

        if (errores.length > 0) {
          producto.estado = 'error';
          producto.mensaje = errores.join(', ');
        } else {
          producto.estado = 'valido';
        }
      }

      this.productosPreview.push(producto);
    });

    this.mostrarPreview = true;
    this.hasMoreData = response.hasMoreData || false;

    // Mostrar información adicional si hay más datos
    if (this.hasMoreData) {
      console.log('Hay más datos disponibles. Se muestran solo los primeros 100 registros en el preview.');
    }
  }

  procesarDatosExcel(data: any[]): void {
    this.productosPreview = [];
    this.totalRegistros = 0;
    this.registrosValidos = 0;
    this.registrosConError = 0;

    // Saltar la primera fila (encabezados)
    for (let i = 1; i < data.length && i <= 101; i++) {
      const row = data[i];
      
      if (!row || row.length === 0) continue;

      const producto: ProductoPreview = {
        codigo: row[0]?.toString() || '',
        codigoBarras: row[1]?.toString() || '',
        nombre: row[2]?.toString() || '',
        tipo: row[3]?.toString() || '',
        stock: parseFloat(row[4]) || 0,
        stockMin: parseFloat(row[5]) || 0,
        stockMax: parseFloat(row[6]) || 0,
        moneda: row[7]?.toString() || '',
        proveedor: row[8]?.toString() || '',
        impuestos: parseFloat(row[9]) || 0,
        iva: parseFloat(row[10]) || 0,
        precioCompraBase: parseFloat(row[11]) || 0,
        precioCompraFinal: parseFloat(row[12]) || 0,
        margen: parseFloat(row[13]) || 0,
        precioVentaBase: parseFloat(row[14]) || 0,
        precioVentaFinal: parseFloat(row[15]) || 0,
        estado: 'pendiente'
      };

      // Validar datos
      const errores: string[] = [];
      
      if (!producto.codigo) errores.push('código requerido');
      if (!producto.nombre) errores.push('nombre requerido');
      if (producto.precioVentaFinal <= 0) errores.push('precio venta inválido');

      if (errores.length > 0) {
        producto.estado = 'error';
        producto.mensaje = errores.join(', ');
        this.registrosConError++;
      } else {
        producto.estado = 'valido';
        this.registrosValidos++;
      }

      this.productosPreview.push(producto);
      this.totalRegistros++;
    }

    this.mostrarPreview = true;
  }

  subirArchivo(): void {
    if (!this.archivoSeleccionado) {
      Swal.fire({
        icon: 'warning',
        title: 'Archivo no seleccionado',
        text: 'Por favor seleccione un archivo Excel primero',
        confirmButtonColor: '#102dd3'
      });
      return;
    }

    if (this.registrosConError > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Registros con errores',
        text: `Hay ${this.registrosConError} registros con errores. Por favor corrija el archivo antes de subirlo.`,
        confirmButtonColor: '#102dd3'
      });
      return;
    }

    Swal.fire({
      title: '¿Confirmar carga?',
      html: `
        <p>Se cargarán <strong>${this.totalRegistros} productos</strong> al sistema.</p>
        <p style="color: #66615b; font-size: 13px;">Esta acción no se puede deshacer.</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#102dd3',
      cancelButtonColor: '#66615b',
      confirmButtonText: 'Sí, cargar productos',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.procesarCargaMasiva();
      }
    });
  }

  procesarCargaMasiva(): void {
    this.procesando = true;

    Swal.fire({
      title: 'Procesando productos',
      html: `
        <p style="margin-top: 20px; color: #66615b; font-size: 14px;">
          Cargando ${this.totalRegistros} productos al sistema...<br>
          <small>Por favor espere, esto puede tomar varios minutos</small>
        </p>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    // Crear FormData limpio con SOLO el archivo
    const formData = new FormData();
    formData.append('file', this.archivoSeleccionado as File, this.archivoSeleccionado.name);

    this.archivoServicio.processArticulosExcel(formData).subscribe(
      response => {
        this.procesando = false;
        Swal.close();

        // Verificar respuesta del backend
        const totalProcesados = response?.totalProcessed || response?.totalRows || this.totalRegistros;
        const exitosos = response?.successCount || response?.validRows || 0;
        const fallidos = response?.errorCount || response?.errorRows || 0;

        Swal.fire({
          icon: 'success',
          title: 'Carga completada',
          html: `
            <p>Los productos se han procesado exitosamente.</p>
            <div style="margin-top: 15px; padding: 15px; background: #f0fdf4; border-radius: 8px; border: 1px solid #6bd098;">
              <p style="color: #66615b; font-size: 13px; margin-bottom: 5px;">
                <strong>Total procesados:</strong> ${totalProcesados}
              </p>
              <p style="color: #6bd098; font-size: 13px; margin-bottom: 5px;">
                <strong>✓ Exitosos:</strong> ${exitosos}
              </p>
              ${fallidos > 0 ? `<p style="color: #ef8157; font-size: 13px; margin-bottom: 0;">
                <strong>✗ Fallidos:</strong> ${fallidos}
              </p>` : ''}
            </div>
          `,
          confirmButtonColor: '#102dd3',
          confirmButtonText: 'Ver productos'
        }).then(() => {
          this.router.navigate(['/productos']);
        });
      },
      error => {
        this.procesando = false;
        Swal.close();

        console.error('Error al cargar productos:', error);
        
        // Extraer información del error
        const errorMessage = error?.error?.message || error?.message || 'Ocurrió un error al procesar el archivo. Por favor, intente nuevamente.';
        const errorDetails = error?.error?.errors || [];

        let errorHtml = `<p>${errorMessage}</p>`;
        
        if (errorDetails.length > 0) {
          errorHtml += `
            <div style="margin-top: 15px; text-align: left; max-height: 200px; overflow-y: auto;">
              <p style="font-weight: 600; color: #66615b; font-size: 13px;">Detalles de errores:</p>
              <ul style="font-size: 12px; color: #66615b;">
                ${errorDetails.slice(0, 10).map((err: any) => 
                  `<li>Fila ${err.row}: ${err.errors?.join(', ') || err.message}</li>`
                ).join('')}
                ${errorDetails.length > 10 ? `<li><em>... y ${errorDetails.length - 10} errores más</em></li>` : ''}
              </ul>
            </div>
          `;
        }

        Swal.fire({
          icon: 'error',
          title: 'Error en la carga',
          html: errorHtml,
          confirmButtonColor: '#102dd3',
          width: '600px'
        });
      }
    );
  }

  limpiarArchivo(): void {
    this.archivoSeleccionado = null;
    this.nombreArchivo = '';
    this.productosPreview = [];
    this.mostrarPreview = false;
    this.hasMoreData = false;
    this.totalRegistros = 0;
    this.registrosValidos = 0;
    this.registrosConError = 0;
    
    // Limpiar input file
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  descargarPlantilla(): void {
    Swal.fire({
      title: 'Descargando plantilla',
      html: `
        <p style="margin-top: 20px; color: #66615b; font-size: 14px;">
          Preparando archivo de plantilla...<br>
          <small>Por favor espere</small>
        </p>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    this.archivoServicio.getArticulosExcel().subscribe(
      response => {
        Swal.close();
        const fileName = `plantilla_productos_${new Date().getTime()}.xlsx`;
        this.descargarArchivo(response, fileName);

        Swal.fire({
          icon: 'success',
          title: 'Plantilla descargada',
          text: 'Use esta plantilla como guía para cargar sus productos',
          timer: 2000,
          showConfirmButton: false
        });
      },
      error => {
        Swal.close();
        console.error('Error al descargar plantilla:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error al descargar',
          text: 'No se pudo descargar la plantilla. Intente nuevamente.',
          confirmButtonColor: '#102dd3'
        });
      }
    );
  }

  private descargarArchivo(response: any, fileName: string): void {
    const dataType = response.type;
    const binaryData = [];
    binaryData.push(response);

    const filePath = window.URL.createObjectURL(
      new Blob(binaryData, { type: dataType })
    );
    const downloadLink = document.createElement('a');
    downloadLink.href = filePath;
    downloadLink.setAttribute('download', fileName);
    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
  }

  volver(): void {
    this.router.navigate(['/productos']);
  }

}
